import ImageIcon from 'atoms/icons/ImageIcon'
import InfoIcon from 'atoms/icons/InfoIcon'
import RefreshIcon from 'atoms/icons/RefreshIcon'
import SearchIcon from 'atoms/icons/SearchIcon'
import TextIcon from 'atoms/icons/TextIcon'
import { useAtom } from 'jotai'
import ProductInfoModal from 'molecules/ProductInfoModal'
import { useEffect, useMemo, useRef, useState } from 'react'
import { higherZIndexAtom, itemsDataAtom, productAtom, selectedItemIdAtom } from 'store/atoms'
import { createImage, createText } from 'store/factory'
import { ItemDataTypes, ItemType, Product } from 'store/types'
import { currencyStringToString } from 'utils/currency'
import { useGetProductsByName } from 'service/hooks/useGetProductsByName'
import { useGetProduct } from 'service/hooks/useGetProduct'
import { useDebounce } from 'custom-hooks/useDebounce'
import { useGetRandomProduct } from 'service/hooks/useGetRandomProduct'
import Image from 'atoms/Image'

const parseHTMLString = (value: string) =>
  value
    .replaceAll('\n', '')
    .replaceAll('<br>', '\n\n')
    .replaceAll('<br />', '\n\n')
    .replaceAll('<br/>', '\n\n')
    .replace(/<[^>]*>/g, '')

type RelevantProduct =
  | {
      [key in keyof Partial<Product>]: {
        type: Exclude<ItemType, 'shape'>
        label: string
        value: unknown
      }
    }
  | undefined

function ProductTools() {
  const [_, setItemsData] = useAtom(itemsDataAtom)
  const [__, setSelectedItemId] = useAtom(selectedItemIdAtom)
  const [higherZIndex, setHigherZIndex] = useAtom(higherZIndexAtom)
  const [productId, setProductId] = useAtom(productAtom)

  const { data: product } = useGetProduct({ id: productId })

  const [isOpen, setIsOpen] = useState(false)
  const [productInfoModalOpen, setProductInfoModalOpen] = useState(false)
  const [searchInputValue, setSearchInputValue] = useState('')
  const debouncedSearchInputValue = useDebounce(searchInputValue, 1000)

  const [totalProducts, setTotalProducts] = useState<number>()

  const {
    data: randomProductData,
    isPending: isRandomProductPending,
    mutate: mutateRandomProduct
  } = useGetRandomProduct()

  useEffect(() => {
    if (!randomProductData) return
    setTotalProducts(randomProductData.total)
    setProductId(randomProductData.product.id)
  }, [randomProductData])

  const { data: searchedProducts, isLoading: isSearchedProductsLoading } = useGetProductsByName({
    name: debouncedSearchInputValue
  })

  const inputRef = useRef<HTMLInputElement>(null)

  const relevantProduct: RelevantProduct = useMemo(() => {
    if (!product) return undefined
    return {
      item_identification_name: {
        type: 'text',
        label: 'Nome',
        value: product.item_identification_name
      },
      item_price_price: {
        type: 'text',
        label: 'Preço',
        value: currencyStringToString(product.item_price_price)
      },
      item_image: {
        type: 'image',
        label: 'Url',
        value: product.item_image[0]?.source
      },
      descricao_5_4: {
        type: 'text',
        label: 'Descrição',
        value: product.descricao_5_4 ? parseHTMLString(product.descricao_5_4) : undefined
      }
    }
  }, [product])

  const addItem = (item: ItemDataTypes) => {
    const zIndex = higherZIndex + 1
    item.zIndex = zIndex
    setItemsData(oldState => ({
      ...oldState,
      [item.id]: {
        ...item
      }
    }))
    setHigherZIndex(zIndex)
    setSelectedItemId(item.id)
  }

  const isLoading = isRandomProductPending || isSearchedProductsLoading

  return (
    <>
      <div className='col-span-2 card bg-gray-100'>
        <div className='card-body p-4'>
          <div className='divider'>Produto</div>
          {isLoading ? <progress className='progress progress-primary w-full'></progress> : <div className='h-2' />}
          <div className='flex flex-row gap-2'>
            <div className='dropdown flex-1'>
              <label className='input input-bordered flex items-center gap-2'>
                <span className='text-gray-400'>
                  <SearchIcon size={16} />
                </span>
                <input
                  ref={inputRef}
                  tabIndex={0}
                  type='text'
                  className='grow'
                  placeholder='Pesquisar'
                  value={searchInputValue}
                  onChange={e => setSearchInputValue(e.target.value)}
                />
              </label>
              <ul
                tabIndex={0}
                className={`dropdown-content menu bg-base-100 rounded-box z-[2] w-full p-2 shadow ${searchedProducts?.length ? '' : '!opacity-0'}`}>
                {searchedProducts?.map(p => (
                  <li key={p.id}>
                    <a
                      onClick={() => {
                        ;(document.activeElement as any)?.blur?.()
                        setProductId(p.id)
                      }}>
                      <Image className='size-12 h-fit' src={p.item_image?.[0]?.source} alt='' />
                      {`${p.item_identification_name} (${p.brand})`}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <button
              className='btn btn-primary'
              disabled={isLoading}
              onClick={() => mutateRandomProduct({ total: totalProducts })}>
              <RefreshIcon size={18} />
            </button>
          </div>
          {relevantProduct && (
            <div className='collapse collapse-arrow w-full bg-base-100 rounded-lg'>
              <input
                type='checkbox'
                className='peer'
                value={`${isOpen}`}
                onChange={e => setIsOpen(e.target.value !== 'true')}
              />
              <div className='collapse-title font-bold'>{`${relevantProduct.item_identification_name?.value}`}</div>
              <div className='collapse-content px-0'>
                <div>
                  <table className='table'>
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Chave</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(relevantProduct).map(([key, item]) => (
                        <tr
                          key={key}
                          className='cursor-pointer hover:text-primary-content hover:bg-primary'
                          onClick={async () => {
                            let newItem: ItemDataTypes
                            switch (item.type) {
                              case 'text':
                                newItem = createText()
                                newItem.props.text = item.value as string
                                break
                              case 'image':
                                newItem = createImage()
                                newItem.props.file = item.value as string
                                break
                            }
                            addItem(newItem)
                          }}>
                          <td>
                            <div className='h-full flex justify-center items-center'>
                              {(() => {
                                switch (item.type) {
                                  case 'text':
                                    return <TextIcon />
                                  case 'image':
                                    return <ImageIcon />
                                }
                              })()}
                            </div>
                          </td>
                          <td>{item.label}</td>
                          <td className='max-w-64 whitespace-pre-wrap break-words'>
                            {typeof item.value === 'string' ? item.value : JSON.stringify(item.value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className='px-4 pt-4 flex items-center'>
                    <button
                      className='flex-1 btn btn-primary btn-outline'
                      onClick={() => setProductInfoModalOpen(true)}>
                      <InfoIcon />
                      Mais
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {product && (
        <ProductInfoModal
          product={product}
          open={productInfoModalOpen}
          onClose={() => setProductInfoModalOpen(false)}
          onConfirm={newItem => {
            setProductInfoModalOpen(false)
            addItem(newItem)
          }}
        />
      )}
    </>
  )
}

export default ProductTools
